import React, { useState, useEffect, useRef } from 'react';
import { X, Camera } from 'lucide-react';
import { getImageUrlCached } from '../services/imageService';
import '../styles/Dialog.css';
import '../styles/Buttons.css';

interface ItemFormDialogProps {
  item?: {
    name?: string;
    location?: string;
    type?: string;
    size?: string;
    modelNumber?: string;
    finish?: string;
    selection?: string;
    trade?: string;
    constructionNotes?: string;
    imageId?: string;
  } | null;
  onClose: () => void;
  onSave: (item: any) => void;
  workspaceId: string;
}

const ItemFormDialog: React.FC<ItemFormDialogProps> = ({ item, onClose, onSave, workspaceId }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    type: '',
    size: '',
    modelNumber: '',
    finish: '',
    selection: '',
    trade: '',
    constructionNotes: '',
    imageId: ''
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadItemData = async () => {
      if (item) {
        setFormData({
          name: item.name || '',
          location: item.location || '',
          type: item.type || '',
          size: item.size || '',
          modelNumber: item.modelNumber || '',
          finish: item.finish || '',
          selection: item.selection || '',
          trade: item.trade || '',
          constructionNotes: item.constructionNotes || '',
          imageId: item.imageId || ''
        });

        // Fetch presigned URL for existing image
        if (item.imageId && workspaceId) {
          try {
            const url = await getImageUrlCached(workspaceId, item.imageId);
            setImagePreview(url);
          } catch (err) {
            console.error('Error loading item image:', err);
          }
        }
      }
    };

    loadItemData();
  }, [item, workspaceId]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...formData, imageFile });
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="dialog-header">
          <h2>{item ? 'Edit Item' : 'Add New Item'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} id="item-form">
          <div className="dialog-body" style={{ maxHeight: 'calc(90vh - 160px)' }}>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Item Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>
                  Location <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Material / Type:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Size:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.size}
                  onChange={(e) => handleChange('size', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Model / Category Number / Code:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.modelNumber}
                  onChange={(e) => handleChange('modelNumber', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Finish:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.finish}
                  onChange={(e) => handleChange('finish', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Selection / Color:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.selection}
                  onChange={(e) => handleChange('selection', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Trade :</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.trade}
                  onChange={(e) => handleChange('trade', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Construction Notes :</label>
              <textarea
                className="form-input"
                value={formData.constructionNotes}
                onChange={(e) => handleChange('constructionNotes', e.target.value)}
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <div
                onClick={handleImageClick}
                style={{
                  width: '240px',
                  height: '160px',
                  border: '2px dashed #D1D5DB',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#F9FAFB',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {imagePreview ? (
                  <>
                    <img
                      src={imagePreview}
                      alt="Item preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick();
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Camera size={16} />
                    </button>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      margin: '0 auto 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="60" height="60" viewBox="0 0 80 80" fill="none">
                        <rect x="10" y="15" width="60" height="50" rx="4" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="25" cy="30" r="5" fill="currentColor"/>
                        <path d="M15 50 L30 35 L45 50 L60 30 L70 40 V60 H10 V50" fill="currentColor" opacity="0.3"/>
                      </svg>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '500' }}>NO IMAGE AVAILABLE</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className="dialog-footer">
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="item-form"
            className="btn btn-primary btn-md"
          >
            {item ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemFormDialog;
